import { SECONDARY_CONTACT_TAB, isSecondaryContactMissing } from "./secondaryContact";
// Müşteri profilinin ne kadarının doldurulduğunu hesaplar.
// Kaynak alanlar, Profil ekranındaki (Temel Bilgiler + Faturalandırma) alanlarla birebir aynıdır;
// orada olmayan/opsiyonel alanlar (Temel Bilgiler'deki "İkincil İrtibat No") puanlamaya dahil
// edilmez, aksi halde kullanıcı hiçbir zaman %100'e ulaşamaz. AYRI olan "İkincil İrtibat Kişisi"
// sekmesi ise ZORUNLUDUR ve puana katılır (bkz. secondaryContact parametresi).

const filled = (value) => String(value ?? "").trim().length > 0;

// Profil ekranındaki yapı: { type, individual: {...}, corporate: {...} }.
// ESKİ kayıtlarda yalnızca aktif tipin alanları kök seviyededir.
export function parseBillingInfo(raw) {
  let parsed = null;
  try {
    parsed = typeof raw === "string" ? JSON.parse(raw) : raw || null;
  } catch {
    parsed = null;
  }
  if (!parsed || typeof parsed !== "object") return null;

  const isCorporate = parsed?.type === "corporate";
  return {
    type: isCorporate ? "corporate" : "individual",
    individual: parsed?.individual || (!isCorporate ? parsed : null) || {},
    corporate: parsed?.corporate || (isCorporate ? parsed : null) || {},
  };
}

// Fatura bilgisi (seçili tipe göre) eksiksiz mi? Teklif sonrası uyarı da bu kuralı kullanır.
export function isBillingComplete(info, nationality) {
  const billing = parseBillingInfo(info?.billing);
  if (!billing) return false;

  if (billing.type === "corporate") {
    const c = billing.corporate;
    return filled(c?.companyTitle) && filled(c?.taxOffice) && filled(c?.taxNumber) && filled(c?.address);
  }

  const b = billing.individual;
  const needsTckn = String(nationality || "tc") === "tc";
  return filled(b?.fullName) && filled(b?.email) && filled(b?.address) && (!needsTckn || filled(b?.tckn));
}

// Sözleşme bilgileri (billing JSON'undaki "contract" bloğu) eksiksiz mi? Checkbox kaldırıldığı
// için sözleşme AYRI doldurulur; blok boş/eksikse tamam sayılmaz (sözleşme imza kapısı bunu ilk
// sırada kontrol eder). Kurallar fatura ile aynı; kurumsalda ayrıca imza yetkilisi zorunlu.
export function isContractComplete(info, nationality) {
  let parsed = null;
  try {
    parsed = typeof info?.billing === "string" ? JSON.parse(info.billing) : info?.billing || null;
  } catch {
    parsed = null;
  }
  const contract = parsed && typeof parsed === "object" ? parsed.contract : null;
  if (!contract || typeof contract !== "object") return false;

  if (contract.type === "corporate") {
    const c = contract.corporate || {};
    return filled(c.companyTitle) && filled(c.taxOffice) && filled(c.taxNumber) && filled(c.address)
      && filled(c.authorizedName) && filled(c.authorizedLastname);
  }
  const b = contract.individual || {};
  const needsTckn = String(nationality || "tc") === "tc";
  return filled(b.fullName) && filled(b.email) && filled(b.address) && (!needsTckn || filled(b.tckn));
}

// Belgeler sekmesindeki tipler (ProfilePage.DOCUMENT_TYPES ile aynı) — eksikse puana girer.
const DOCUMENT_LABELS = {
  id_front: "Kimlik Ön Yüzü",
  id_back: "Kimlik Arka Yüzü",
  ticaret_sicil_gazetesi: "Ticaret Sicil Gazetesi",
  tax_plate: "Vergi Levhası",
  signature_circular: "İmza Sirküsü",
};

// Bireysel müşteride yalnız kimlik ön/arka istenir; şirket evrakları KURUMSAL fatura tipinde.
const INDIVIDUAL_DOCUMENT_TYPES = new Set(["id_front", "id_back"]);

// Fatura tipine göre BEKLENEN belge tipleri (kurumsal → hepsi, bireysel → kimlik ön/arka).
export function getExpectedDocumentTypes(info) {
  const isCorporate = parseBillingInfo(info?.billing)?.type === "corporate";
  return Object.keys(DOCUMENT_LABELS).filter(
    (docType) => isCorporate || INDIVIDUAL_DOCUMENT_TYPES.has(docType)
  );
}

// Fatura tipine göre istenen belgelerin TAMAMI yüklü mü?
// documents: get-user-documents sonucundaki "documents" dizisi. null/dizi değilse (henüz
// çekilmedi / istek başarısız) `false` döner — çağıran tarafın "bilinmiyor"u ayrıca ele alması
// beklenir (yanlış engelleme yapmamak için).
export function areRequiredDocumentsComplete(info, documents) {
  if (!Array.isArray(documents)) return false;
  const uploadedByType = new Map(
    documents.filter((doc) => doc?.docType).map((doc) => [doc.docType, doc])
  );
  return getExpectedDocumentTypes(info).every((docType) =>
    filled(uploadedByType.get(docType)?.fileURL)
  );
}

// consents: flow/get-user-consents sonucundaki "consents" dizisi. Zorunlu (required) onaylar
// işaretlenmediği sürece eksik sayılır — liste DİNAMİKTİR, yeni bir zorunlu onay tanımlanınca
// burada ayrıca bir şey yapmak gerekmez.
// documents: flow/get-user-documents sonucundaki "documents" dizisi (null ise puana katılmaz —
// henüz yüklenmemiş/başarısız istek yüzünden oran yanlış düşmesin).
// Her maddenin target'ı Profil ekranındaki sekme anahtarıdır (basic|billing|permissions|documents);
// Sidebar eksik maddeye tıklanınca o sekmeye yönlendirir.
// secondaryContact: kullanıcı bilgisindeki secondaryContacts listesi (admin + müşteri ortak). undefined
// verilirse (henüz çekilmedi / çağıran kullanmıyor) madde puana HİÇ katılmaz.
// { percent, completed, total, missing: [{label, target}] }
// FE-74 (21.10.2026): "Mağaza Kullanıcısı" (/store'dan kendi kaydını açan; current_status 18, channel "magaza")
// depolama müşterisi değildir: kimlik ön/arka, fatura, sözleşme bilgileri ve ikincil irtibat kişisi ona SORULMAZ.
// Teklif kabul edip depolama müşterisine dönünce statüsü değişir ve tam liste kendiliğinden döner.
export function isStoreOnlyUser(info) {
  const status = String(info?.currentStatus ?? info?.current_status ?? "").trim();
  return status === "18" || String(info?.channel ?? "").trim() === "magaza";
}

export function getCustomerProfileCompletion(info, consents = [], documents = null, secondaryContact = undefined) {
  if (!info) return null;
  const storeOnly = isStoreOnlyUser(info);

  const nationality = info?.nationality || "tc";
  const consentItems = (Array.isArray(consents) ? consents : [])
    .filter((consent) => consent?.required)
    .map((consent) => ({
      label: consent?.title || "Onay",
      done: Boolean(consent?.accepted),
      target: "permissions",
    }));

  const isCorporate = parseBillingInfo(info?.billing)?.type === "corporate";
  // Belge maddeleri BEKLENEN TİP LİSTESİNDEN kurulur (gelen kayıtlardan değil): uç yalnız
  // YÜKLENMİŞ belgeleri döndürdüğü için hiç yüklenmemiş belge listeye hiç girmiyor, ne
  // toplama ne eksiklere sayılıyordu — profil %100 görünüyordu.
  const uploadedByType = new Map(
    (Array.isArray(documents) ? documents : [])
      .filter((doc) => doc?.docType)
      .map((doc) => [doc.docType, doc])
  );
  const expectedDocumentTypes = Object.keys(DOCUMENT_LABELS)
    .filter((docType) => isCorporate || INDIVIDUAL_DOCUMENT_TYPES.has(docType));
  // documents null ise (henüz çekilmedi / istek başarısız) belgeler puana hiç katılmaz.
  const documentItems = Array.isArray(documents)
    ? expectedDocumentTypes.map((docType) => ({
        label: DOCUMENT_LABELS[docType],
        done: filled(uploadedByType.get(docType)?.fileURL),
        target: "documents",
      }))
    : [];

  const basicItems = [
    { label: "Ad Soyad", done: filled(info?.name) && filled(info?.lastname), target: "basic" },
    { label: "E-posta", done: filled(info?.email), target: "basic" },
    { label: "Telefon", done: filled(info?.phone), target: "basic" },
    { label: "Profil Fotoğrafı", done: filled(info?.avatar), target: "basic" },
  ];
  const items = storeOnly ? [...basicItems, ...consentItems] : [
    ...basicItems,
    { label: "Fatura Bilgileri", done: isBillingComplete(info, nationality), target: "billing" },
    { label: "Sözleşme Bilgileri", done: isContractComplete(info, nationality), target: "contract" },
    // İkincil irtibat kişisi ZORUNLU (28.09.2026): eklenmemişse eksik.
    ...(secondaryContact === undefined
      ? []
      : [{ label: "İkincil İrtibat Kişisi", done: !isSecondaryContactMissing(secondaryContact), target: SECONDARY_CONTACT_TAB }]),
    ...consentItems,
    ...documentItems,
  ];

  const completed = items.filter((item) => item.done).length;
  return {
    percent: Math.round((completed / items.length) * 100),
    completed,
    total: items.length,
    missing: items.filter((item) => !item.done).map(({ label, target }) => ({ label, target })),
  };
}
